import React, {useEffect, useMemo, useState} from 'react';
import ReactDOM from "react-dom/client";
import {Question} from "survey-core";
import DropdownMultiGroupSelect from "patient-ping-remedy/packages/dropdown-multi-group";
import {nonGroupedValues} from "@common/FilterComponent";
import InfoIconTooltip from "@common/InfoIconTooltip";
import {colors, sizes} from "patient-ping-remedy/packages/theme";
import Spacer from "patient-ping-remedy/packages/spacer";
import {Wrapper} from "@common/survey_js/CustomMultiSelectWidget.styles";

const CustomMultiSelectDropdown: React.FC<{ question: Question }> = ({ question }) => {
  const [selectedValue, setSelectedValue] = useState<nonGroupedValues[]>([]);

  const handleChange = (value : any) => {
    setSelectedValue(value);
    question.value = value.map((item : any) => item.value);
  };

  const handleQuestionChange = () => {
    const currentValue = question.value ? question.value.map((item: any) => {
      const choice = question.choices.find((c: any) => c.value === item);
      const label = choice ? (choice.text || choice.value) : item;
      return { id: item, label: label, value: item };
    }) : [];
    setSelectedValue(currentValue);
  };

  useEffect(() => {
    handleQuestionChange();
    question.valueChangedCallback = handleQuestionChange;
  }, []);

  const options = useMemo(() => {
    return question.choices.map((choice : any) => {
      return {id: choice.id, label: choice.text || choice.value, value: choice.value};
    });
  }, []);

  return (
    <Wrapper data-testid={"dropdown-multiselect"} aria-disabled={question.readOnly}>
      <DropdownMultiGroupSelect
        id={"multiselect"}
        label={(
          <>
            <span>{question.title}</span>
            {question.tooltip && (
              <>
                <Spacer itemWidth={sizes.xSmall}/>
                <InfoIconTooltip color={colors.charcoal} text={question.tooltip} />
              </>
            )}
          </>
        ) as unknown as string}
        handleChange={handleChange}
        options={options}
        value={selectedValue}
        isRequiredField={question.isRequired}
        isDisabled={question.readOnly}
      />
    </Wrapper>
  );
};

export type CustomMultiSelectDropdownQuestion = Question & {
  __multiselectRoot?: ReactDOM.Root;
}

export const CustomMultiSelectWidget = {
  name: "custom-multiselect",
  title: "Custom Multiselect dropdown",
  widgetIsLoaded: () => true,
  isFit: (question: CustomMultiSelectDropdownQuestion) =>
    question.getType() === "tagbox" && question.customComponent === "custom-multiselect",
  afterRender: (question: CustomMultiSelectDropdownQuestion, el: HTMLElement) => {
    if (!question.__multiselectRoot) {
      const container = document.createElement('div');
      el.appendChild(container);
      question.__multiselectRoot = ReactDOM.createRoot(container);
    }
    question.__multiselectRoot.render(<CustomMultiSelectDropdown question={question} />);
  },
  willUnmount: (question: CustomMultiSelectDropdownQuestion) => {
    if (question.__multiselectRoot) {
      const root = question.__multiselectRoot;
      question.__multiselectRoot = undefined;
      setTimeout(() => {
        root.unmount();
      });
    }
  },
};
